import { useRouter } from 'next/router';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import type { BookingItem, BookingListItem } from 'types/booking';
import type { BookingFilters, Pagination } from 'types/filters';
import type dayjs from 'utils/dayjs';
import { aggItemsToCheckboxFilterItem } from 'utils/dynamicFilters';
import { combineReducers } from 'utils/reducers';
import { CANCELLED, CONFIRMED, PROVISIONAL } from '../../constants/bookings';
import { useApiRequest } from '../../hooks/useApiRequest';
// hooks
import { useInterval } from '../../hooks/useInterval';
import { filterActions, filtersReducer } from '../../reducers/filters';
import {
  type Report,
  reportsActions,
  reportsReducer,
} from '../../reducers/reports';
import bookingsParamsBuilder, {
  filtersQueryBuilder,
} from '../../services/bookingsQueryBuilder';
import { AuthContext } from '../auth';
import reducer, { actions, initialiseState, initialState } from './reducer';

export interface BookingsActions {
  fetchBookings: () => Promise<void>;
  fetchBookingItems: () => Promise<void>;
  clearReport: () => Promise<void>;
  createReport: () => Promise<void>;
  fetchReport: () => Promise<void>;
  fetchAllBookingsMetadata: () => Promise<void>;
  fetchAllBookingItemsMetadata: () => Promise<void>;
  setSelectedItem: (
    filterKey: string,
    value: string | number,
    checked: boolean
  ) => void;
  // Curried: DynamicSelectFilter calls these with the group name and uses the
  // returned function as the button's click handler.
  uncheckFilters: (filterName: string) => () => void;
  checkAllFilters: (filterName: string) => () => void;
  clearFilters: () => void;
  enableFilters: () => void;
  disableFilters: () => void;
  handleSortByChange: (newSortBy: string) => void;
  handleKeywordSearch: (value: string) => void;
  applyFilters: () => void;
  changePagination: (page: number, perPage: number) => void;
  handleStartDateChange: (startDate?: dayjs.ConfigType) => void;
  handleEndDateChange: (endDate?: dayjs.ConfigType) => void;
  removeBooking: (
    id: number,
    reason?: string,
    reasonMessage?: string
  ) => Promise<void> | void;
  removeBookingItems: (
    bookingId: number,
    ids: number[],
    reason?: string,
    reasonMessage?: string
  ) => Promise<void> | void;
}

export interface BookingsContextValue {
  actions: BookingsActions;
  bookings: BookingListItem[];
  bookingItems: BookingItem[];
  loading: boolean;
  metadataLoaded: boolean;
  filtersEnabled: boolean;
  sortBy: string;
  filters: BookingFilters;
  totalCount: number | string;
  count: number | string;
  allBookingsCount: number | string;
  initialized: boolean;
  pagination: Pagination;
  report: Report;
}

export const BookingsContext = createContext<BookingsContextValue>({
  loading: false,
  metadataLoaded: false,
  ...initialState,
  actions: {
    fetchBookings: async () => {},
    fetchBookingItems: async () => {},
    clearReport: async () => {},
    createReport: async () => {},
    fetchReport: async () => {},
    fetchAllBookingsMetadata: async () => {},
    fetchAllBookingItemsMetadata: async () => {},
    setSelectedItem: () => {},
    uncheckFilters: () => () => {},
    checkAllFilters: () => () => {},
    clearFilters: () => {},
    enableFilters: () => {},
    disableFilters: () => {},
    handleSortByChange: () => {},
    handleKeywordSearch: () => {},
    applyFilters: () => {},
    changePagination: () => {},
    handleStartDateChange: () => {},
    handleEndDateChange: () => {},
    removeBooking: async () => {},
    removeBookingItems: () => {},
  },
});

const combinedReducers = combineReducers(
  filtersReducer,
  reportsReducer,
  reducer
);

const isBookingsPage = (pathname = '/') => {
  const [first, second] = pathname.slice(1).split('/');
  return first === 'my-account' && second === 'bookings';
};

const BookingsProvider = ({ children }: { children: ReactNode }) => {
  const [
    {
      bookings,
      bookingItems,
      filtersEnabled,
      sortBy,
      filters,
      totalCount,
      count,
      allBookingsCount,
      initialized,
      pagination,
      report,
    },
    dispatch,
  ] = useReducer(combinedReducers, initialState, initialiseState);

  const [loading, setLoading] = useState(true);
  const [metadataLoaded, setMetadataLoaded] = useState(false);

  const router = useRouter();

  useEffect(() => {
    dispatch({
      type: isBookingsPage(router.pathname)
        ? actions.RESET_EXCEPT_FILTERS
        : actions.RESET,
    });
  }, [router.pathname]);

  const apiRequest = useApiRequest();

  // Serialise the parameters so that the hooks only fire when the
  // actual content changes
  const bookingsParamsJson = useMemo(
    () => JSON.stringify(bookingsParamsBuilder(filters, sortBy, pagination)),
    [filters, sortBy, pagination]
  );

  const fetchBookings = useCallback(async () => {
    setLoading(true);

    const {
      data: { bookings: apiItems, meta },
    } = await apiRequest.get('/v1/bookings', {
      params: JSON.parse(bookingsParamsJson),
    });

    dispatch({
      type: actions.FETCH_BOOKINGS_SUCCESS,
      payload: {
        bookings: apiItems,
        meta,
      },
    });

    setLoading(false);
  }, [bookingsParamsJson, apiRequest]);

  const fetchBookingItems = useCallback(async () => {
    setLoading(true);

    const {
      data: { bookingItems: apiItems, meta },
    } = await apiRequest.get('/v1/booking_items', {
      params: JSON.parse(bookingsParamsJson),
    });

    dispatch({
      type: actions.FETCH_BOOKING_ITEMS_SUCCESS,
      payload: {
        bookingItems: apiItems,
        meta,
      },
    });

    setLoading(false);
  }, [bookingsParamsJson, apiRequest]);

  const clearReport = useCallback(async () => {
    dispatch({ type: reportsActions.CLEAR_REPORT });
  }, []);

  const filtersQueryJson = useMemo(
    () => JSON.stringify(filtersQueryBuilder(filters)),
    [filters]
  );

  const createReport = useCallback(async () => {
    const { data: apiReport } = await apiRequest.post(
      '/v1/booking_items/create_report',
      JSON.parse(filtersQueryJson)
    );

    dispatch({
      type: reportsActions.CREATE_REPORT,
      payload: {
        report: apiReport,
      },
    });
  }, [filtersQueryJson, apiRequest]);

  const fetchReport = useCallback(async () => {
    const { data: apiReport } = await apiRequest.get(
      `/v1/reports/${report.id}`
    );

    dispatch({
      type: reportsActions.CREATE_REPORT,
      payload: {
        report: apiReport,
      },
    });
  }, [report.id, apiRequest]);

  const fetchAllBookingsMetadata = useCallback(async () => {
    const {
      data: {
        meta: {
          pagination: { totalCount: apiAllBookingsCount },
          aggs,
        },
      },
    } = await apiRequest.get(`/v1/bookings`, {
      params: { perPage: 1 },
    });
    const statuses = [{ id: CONFIRMED }, { id: CANCELLED }, { id: PROVISIONAL }]
      .map(status => {
        return {
          ...status,
          name: status.id.replace(/^\w/, c => c.toUpperCase()),
        };
      })
      .map(aggItemsToCheckboxFilterItem);
    const createdByUsers = aggs.createdByUsers.map(
      aggItemsToCheckboxFilterItem
    );
    const venues = aggs.venues.map(aggItemsToCheckboxFilterItem);
    const spaces = aggs.spaces.map(aggItemsToCheckboxFilterItem);
    const services = aggs.services.map(aggItemsToCheckboxFilterItem);
    const paymentStatuses = aggs.paymentStatuses.map(
      aggItemsToCheckboxFilterItem
    );
    dispatch({
      type: actions.FETCH_ALL_BOOKINGS_METADATA,
      payload: {
        allBookingsCount: apiAllBookingsCount,
        filters: {
          statuses,
          createdByUsers,
          venues,
          spaces,
          services,
          paymentStatuses,
        },
      },
    });

    setMetadataLoaded(true);
  }, [apiRequest]);

  const fetchAllBookingItemsMetadata = useCallback(async () => {
    const {
      data: {
        meta: {
          pagination: { totalCount: apiAllBookingItemsCount },
          aggs,
        },
      },
    } = await apiRequest.get(`/v1/booking_items`, {
      params: { perPage: 1 },
    });
    const statuses = aggs.statuses.map(aggItemsToCheckboxFilterItem);
    const createdByUsers = aggs.createdByUsers.map(
      aggItemsToCheckboxFilterItem
    );
    const venues = aggs.venues.map(aggItemsToCheckboxFilterItem);
    const spaces = aggs.spaces.map(aggItemsToCheckboxFilterItem);
    const services = aggs.services.map(aggItemsToCheckboxFilterItem);
    const paymentStatuses = aggs.paymentStatuses.map(
      aggItemsToCheckboxFilterItem
    );

    dispatch({
      type: actions.FETCH_ALL_BOOKING_ITEMS_METADATA,
      payload: {
        allBookingItemsCount: apiAllBookingItemsCount,
        filters: {
          statuses,
          createdByUsers,
          venues,
          spaces,
          services,
          paymentStatuses,
        },
      },
    });

    setMetadataLoaded(true);
  }, [apiRequest]);

  const setSelectedItem = useCallback(
    (filterKey: string, value: string | number, checked: boolean) => {
      dispatch({
        type: filterActions.SET_SELECTED_ITEM,
        payload: {
          selectedValue: value,
          filterKey,
          checked,
        },
      });
    },
    []
  );

  const uncheckFilters = useCallback((filterName: string) => {
    return () => {
      dispatch({
        type: filterActions.UNCHECK_FILTERS,
        payload: {
          filterName,
        },
      });
    };
  }, []);

  const checkAllFilters = useCallback((filterName: string) => {
    return () => {
      dispatch({
        type: filterActions.CHECK_ALL_FILTERS,
        payload: {
          filterName,
        },
      });
    };
  }, []);

  const clearFilters = useCallback(() => {
    dispatch({
      type: filterActions.CLEAR_FILTERS,
    });
  }, []);

  const enableFilters = useCallback(() => {
    dispatch({
      type: filterActions.ENABLE_FILTERS,
    });
  }, []);

  const disableFilters = useCallback(() => {
    dispatch({
      type: filterActions.DISABLE_FILTERS,
    });
  }, []);

  const handleSortByChange = useCallback((newSortBy: string) => {
    dispatch({
      type: actions.SET_SORT_BY,
      payload: {
        sortBy: newSortBy,
      },
    });
  }, []);

  const handleKeywordSearch = useCallback((value: string) => {
    dispatch({
      type: filterActions.SET_KEYWORD_FILTER,
      payload: { keyword: value },
    });
  }, []);

  const applyFilters = useCallback(() => {
    dispatch({
      type: filterActions.APPLY_FILTERS,
      payload: {
        checkBoxFilterNames: [
          'services',
          'spaces',
          'venues',
          'createdByUsers',
          'statuses',
          'paymentStatuses',
        ],
      },
    });
  }, []);

  const changePagination = useCallback((page: number, perPage: number) => {
    dispatch({
      type: actions.CHANGE_PAGINATION,
      payload: {
        page,
        perPage,
      },
    });
  }, []);

  const handleStartDateChange = useCallback((startDate?: dayjs.ConfigType) => {
    dispatch({
      type: filterActions.SET_START_DATE,
      payload: { startDate },
    });
  }, []);

  const handleEndDateChange = useCallback((endDate?: dayjs.ConfigType) => {
    dispatch({
      type: filterActions.SET_END_DATE,
      payload: { endDate },
    });
  }, []);

  const removeBooking = useCallback(
    async (id: number, reason?: string, reasonMessage?: string) => {
      await apiRequest.delete(`/v1/bookings/${id}`, {
        params: { reason, reasonMessage },
      });

      fetchBookings();
    },
    [fetchBookings, apiRequest]
  );

  const removeBookingItems = useCallback(
    async (
      bookingId: number,
      ids: number[],
      reason?: string,
      reasonMessage?: string
    ) => {
      await apiRequest.delete(`/v1/bookings/${bookingId}/booking_items`, {
        params: { ids, reason, reasonMessage },
      });

      fetchBookings();
    },
    [apiRequest, fetchBookings]
  );

  const actionMethods = {
    fetchBookings,
    fetchBookingItems,
    clearReport,
    createReport,
    fetchReport,
    fetchAllBookingsMetadata,
    fetchAllBookingItemsMetadata,
    setSelectedItem,
    uncheckFilters,
    checkAllFilters,
    clearFilters,
    enableFilters,
    disableFilters,
    handleSortByChange,
    handleKeywordSearch,
    applyFilters,
    changePagination,
    handleStartDateChange,
    handleEndDateChange,
    removeBookingItems,
    removeBooking,
  };

  return (
    <BookingsContext.Provider
      value={{
        bookings,
        bookingItems,
        actions: actionMethods,
        loading,
        filtersEnabled,
        sortBy,
        filters,
        totalCount,
        count,
        allBookingsCount,
        initialized,
        pagination,
        report,
        metadataLoaded,
      }}
    >
      {children}
    </BookingsContext.Provider>
  );
};

export default BookingsProvider;

export const useBookingsLoader = () => {
  const { loggedIn } = useContext(AuthContext);
  const context = useContext(BookingsContext);
  const {
    metadataLoaded,
    report,
    actions: { fetchBookings, fetchAllBookingsMetadata, fetchReport },
  } = context;

  useEffect(() => {
    if (loggedIn && metadataLoaded) fetchBookings();
  }, [loggedIn, metadataLoaded, fetchBookings]);

  useEffect(() => {
    if (loggedIn) fetchAllBookingsMetadata();
  }, [loggedIn, fetchAllBookingsMetadata]);

  useInterval(
    () => {
      fetchReport();
    },
    1000,
    report.isReady === false
  );

  return context;
};

export const useBookingItemsLoader = () => {
  const { loggedIn } = useContext(AuthContext);
  const context = useContext(BookingsContext);
  const {
    metadataLoaded,
    report,
    actions: { fetchBookingItems, fetchAllBookingItemsMetadata, fetchReport },
  } = context;

  useEffect(() => {
    if (loggedIn && metadataLoaded) fetchBookingItems();
  }, [loggedIn, metadataLoaded, fetchBookingItems]);

  useEffect(() => {
    if (loggedIn) fetchAllBookingItemsMetadata();
  }, [loggedIn, fetchAllBookingItemsMetadata]);

  useInterval(
    () => {
      fetchReport();
    },
    1000,
    report.isReady === false
  );

  return context;
};
